import { useParams, useLocation, Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import RunExplorerPage from './RunExplorerPage';
import { useEndpoint } from '../hooks/useEndpoints';
import { useRunDetail } from '../hooks/useRunExplorer';
import { serviceRunsTabPath } from '../components/runExplorer/runExplorerLinks';
import { testRunSectionPath } from '../components/layout/AssistantTestingCards';
import { endpointDisplayName } from '../utils/endpointLabel';

/**
 * In-context run detail: one of this AI Service's runs, opened from its Runs
 * tab. The page is the Run Explorer (task 1151) inside the service's chrome —
 * a back link to the Runs tab and the run's provenance badges — so both entry
 * points render the same component.
 */
export default function EndpointRunDetailPage() {
  const { id, collaborationId } = useParams<{ id: string; collaborationId: string }>();
  // Test provenance (task 1306): the run detail names the test that produced
  // the run, so the badge survives a refresh or a shared link. The router state
  // a "View run" link passes (task 365) only stands in while the detail loads.
  const location = useLocation();
  const testContext = (location.state ?? null) as { testId?: string; testName?: string } | null;
  // The same query the Run Explorer below reads, so this adds no request.
  const { data: detail, isLoading: detailLoading } = useRunDetail(collaborationId, collaborationId);
  const test = detail?.test ?? null;
  const testLabel = test
    ? `${test.scenarioName}${test.testNumber != null ? ` · #${test.testNumber}` : ''}`
    : detailLoading
      ? testContext?.testName ?? null
      : null;
  // Schedule provenance (task 1003), the parallel phrase: a "View run" link from
  // the Schedules section passes the schedule it fired from, and the header
  // names it with a way back. A run has ONE origin, so this and the test badge
  // never both render. Router state, exactly like the test context above,
  // because nothing on the run says which schedule produced it — only the
  // schedule's own run log does.
  const scheduleContext = (location.state ?? null) as {
    scheduleId?: string;
    scheduleName?: string;
  } | null;
  const { data: endpoint } = useEndpoint(id);

  // Back to the AI Service page's Runs tab (task 1306), not the endpoint hub.
  const runsTabPath = id ? serviceRunsTabPath(id) : '/endpoints';

  const backLink = (
    <Link
      to={runsTabPath}
      className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
    >
      <ChevronLeftIcon className="h-4 w-4 mr-1" />
      {/* Matches the hub header's display name (name → description → method + path). */}
      {endpoint ? endpointDisplayName(endpoint) : 'AI Service'} · Runs
    </Link>
  );

  const badges = (
    <>
      {testLabel && (
        <span
          data-testid="run-produced-by-test"
          className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200"
        >
          produced by test{' '}
          {id && test?.testGroupId ? (
            <Link to={testRunSectionPath(id, test.testGroupId)} className="font-medium underline">
              {testLabel}
            </Link>
          ) : (
            testLabel
          )}
        </span>
      )}
      {scheduleContext?.scheduleName && (
        <span
          data-testid="run-scheduled-by"
          className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
        >
          scheduled by{' '}
          <Link
            to={`/services/${id}?tab=config&section=schedules`}
            className="font-medium underline"
          >
            {scheduleContext.scheduleName}
          </Link>
        </span>
      )}
    </>
  );

  return (
    <RunExplorerPage
      collaborationId={collaborationId}
      runId={collaborationId}
      backLink={backLink}
      badges={badges}
    />
  );
}
